import React, { useMemo, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import StudentHeader from '../components/StudentHeader';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import { TabPanel } from '../components/ui/Tabs';
import StudentTabs from '../components/StudentTabs';
import SafeguardingList from '../components/SafeguardingList';
import SafeguardingSummary from '../components/safeguarding/SafeguardingSummary';
import ActionRequiredList from '../components/safeguarding/ActionRequiredList';
import { getActionRequiredEvents, getSafeguardingSummary } from '../components/safeguarding/safeguardingUtils';
import TimelineView from '../components/TimelineView';
import StudentAttendanceView from '../components/attendance/StudentAttendanceView';
import DocumentsPanel from '../components/DocumentsPanel';
import DateSelect from '../components/ui/DateSelect';
import Modal from '../components/ui/Modal';
import SearchableChecklist from '../components/ui/SearchableChecklist';
import SearchableSelect from '../components/ui/SearchableSelect';
import usePermissions from '../store/usePermissions';
import { useStudentStore } from '../store/StudentStore';
import { useUser } from '../store/UserContext';
import { useSettings } from '../store/SettingsContext';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import StaffService from '../services/StaffService';
import EmailService from '../services/EmailService';
import { buildResetToken, buildPasswordHash } from '../services/PasswordService';
import JournalService from '../services/JournalService';
import PointsService from '../services/PointsService';
import ExternalMessageService from '../services/ExternalMessageService';
import { readFilesAsStoredAttachments } from '../utils/fileAttachments';

const tabs = [
  { id: 'overview', label: 'Overview' },
  { id: 'info', label: 'Student Info' },
  { id: 'safeguarding', label: 'Safeguarding' },
  { id: 'timeline', label: 'Timeline' },
  { id: 'attendance', label: 'Attendance' },
  { id: 'documents', label: 'Documents' },
  { id: 'certificates', label: 'Certificates' },
  { id: 'enrolment', label: 'Activities & Programmes' },
  { id: 'incidents', label: 'Incident Reports' },
  { id: 'journal', label: 'Journal' },
  { id: 'notes', label: 'Notes' },
  { id: 'audit', label: 'Audit Log' }
];

const incidentTypes = ['Injury', 'Safeguarding concern', 'Property damage', 'Theft', 'Behavioural', 'Other'];
const defaultExportOptions = {
  incidents: true,
  attendance: true,
  safeguarding: true,
  notes: true,
  reviews: true,
  documents: true,
  certificates: true,
  enrolments: true,
  activities: true,
  inventory: true,
  statusHistory: true
};

const XLSX_CELL_CHAR_LIMIT = 32767;

const clampExcelCell = (text) => {
  if (text.length <= XLSX_CELL_CHAR_LIMIT) return text;
  const suffix = '...[truncated]';
  return `${text.slice(0, XLSX_CELL_CHAR_LIMIT - suffix.length)}${suffix}`;
};

const sanitizeExportValue = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) {
    return value
      .map((entry) => sanitizeExportValue(entry))
      .filter(Boolean)
      .join('; ');
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value, (key, item) => {
      if (typeof item === 'string' && item.startsWith('data:')) {
        return '[binary omitted]';
      }
      return item;
    });
    return clampExcelCell(json || '');
  }
  return clampExcelCell(String(value));
};

const normalizeCell = (value) => {
  return sanitizeExportValue(value);
};

const buildSheet = (headers, rows, emptyMessage) => {
  const safeRows = rows.length
    ? rows
    : [[emptyMessage, ...Array(Math.max(headers.length - 1, 0)).fill('')]];
  const data = [headers, ...safeRows].map((row) => row.map(normalizeCell));
  return XLSX.utils.aoa_to_sheet(data);
};

const appendSheet = (workbook, name, headers, rows, emptyMessage) => {
  const sheet = buildSheet(headers, rows, emptyMessage);
  XLSX.utils.book_append_sheet(workbook, sheet, name);
};

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

  const buildDateRange = (startValue, endValue) => {
  const start = startValue ? new Date(`${startValue}T00:00:00`) : null;
  const end = endValue ? new Date(`${endValue}T23:59:59.999`) : null;
  return { start, end, startLabel: startValue || '', endLabel: endValue || '' };
};

const isWithinRange = (value, range) => {
  if (!range.start && !range.end) return true;
  const date = parseDateValue(value);
  if (!date) return true;
  if (range.start && date < range.start) return false;
  if (range.end && date > range.end) return false;
  return true;
};

  const isRangeOverlap = (startValue, endValue, range) => {
  if (!range.start && !range.end) return true;
  const startDate = parseDateValue(startValue);
  const endDate = parseDateValue(endValue) || startDate;
  if (!startDate && !endDate) return true;
  const actualStart = startDate || endDate;
  const actualEnd = endDate || startDate;
  if (range.start && actualEnd < range.start) return false;
  if (range.end && actualStart > range.end) return false;
  return true;
  };

export default function StudentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { controller, incidentController, inventoryController, inventoryItems, timelineService } = useStudentStore();
  const { user } = useUser();
  const { settings } = useSettings();
  const permissions = usePermissions();
  const staffService = useMemo(() => new StaffService(), []);
  const emailService = useMemo(() => new EmailService(), []);
  const journalService = useMemo(() => new JournalService(), []);
  const pointsService = useMemo(() => new PointsService(), []);
  const externalMessageService = useMemo(() => new ExternalMessageService(), []);
  const staffList = useMemo(() => staffService.list(), [staffService]);
  const [activeTab, setActiveTab] = useState('overview');
  const [journalEntries, setJournalEntries] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventCategory, setEventCategory] = useState('Social Care');
  const [editingEventId, setEditingEventId] = useState(null);
  const [eventForm, setEventForm] = useState({
    type: '',
    severity: 'medium',
    status: 'open',
    description: '',
    date: new Date().toISOString().slice(0, 10),
    assignedOwner: user?.name || '',
    reviewDate: '',
    escalationLevel: 'L1',
    attachments: ''
  });
  const [eventError, setEventError] = useState('');
  const [eventFiles, setEventFiles] = useState([]);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    title: '',
    dueDate: new Date().toISOString().slice(0, 10),
    assignedTo: user?.name || '',
    status: 'open',
    notes: ''
  });
  const [reviewError, setReviewError] = useState('');
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceForm, setAttendanceForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    status: 'attended',
    programme: '',
    notes: ''
  });
  const [incidentModalOpen, setIncidentModalOpen] = useState(false);
  const [incidentFiles, setIncidentFiles] = useState([]);
  const [incidentInventorySelection, setIncidentInventorySelection] = useState([]);
  const [incidentStaffSelection, setIncidentStaffSelection] = useState([]);
  const [showIncidentInventoryModal, setShowIncidentInventoryModal] = useState(false);
  const [showArchivedCertificates, setShowArchivedCertificates] = useState(false);
  const [editingCertificateId, setEditingCertificateId] = useState(null);
  const [certificateDraftName, setCertificateDraftName] = useState('');
  const [newIncidentInventoryItem, setNewIncidentInventoryItem] = useState({
    name: '',
    type: 'Tech Equipment',
    condition: 'Good'
  });
  const [incidentForm, setIncidentForm] = useState({
    type: 'Behavioural',
    description: '',
    date: new Date().toISOString().slice(0, 16),
    severity: 'medium',
    status: 'open',
    assignedKeyWorker: user?.name || '',
    followUpRequired: true,
    followUpPrompt: '',
    followUpDate: ''
  });
  const [incidentError, setIncidentError] = useState('');
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState(() => {
    const today = new Date();
    const start = new Date();
    start.setDate(today.getDate() - 90);
    return start.toISOString().slice(0, 10);
  });
  const [exportEndDate, setExportEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [exportOptions, setExportOptions] = useState(defaultExportOptions);
  const [exportError, setExportError] = useState('');
  const [portalPassword, setPortalPassword] = useState('');
  const [portalPasswordConfirm, setPortalPasswordConfirm] = useState('');
  const [portalStatus, setPortalStatus] = useState('');
  const [portalSending, setPortalSending] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    if (tab && tabs.some((item) => item.id === tab)) {
      setActiveTab(tab);
    }
  }, [location.search]);

  const student = controller.getStudent(id);
  const attendanceThreshold = settings.attendanceThreshold || 70;
  const siteName = settings.siteName || 'SafeguardPro';

  useEffect(() => {
    if (!student) {
      setJournalEntries([]);
      return;
    }
    setJournalEntries(journalService.listForStudent(student.id));
  }, [journalService, student]);

  const timelineEvents = useMemo(() => {
    if (!student) return [];
    return timelineService.buildForStudent(student, permissions);
  }, [student, permissions, timelineService]);

  const inventoryLookup = useMemo(
    () =>
      inventoryItems.reduce((acc, item) => {
        acc[item.id] = item.name;
        return acc;
      }, {}),
    [inventoryItems]
  );

  const goToSafeguardingTab = () => {
    setActiveTab('safeguarding');
    navigate(`/students/${student.id}/profile?tab=safeguarding`, { replace: true });
  };

  if (!student) {
    return (
      <Card className="p-6">
        <p className="sp-muted">Student not found.</p>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/students')}>
          Back to students
        </Button>
      </Card>
    );
  }

  const handleSensitiveEdit = (event) => {
    if (!permissions.canEditSensitive()) {
      window.alert('You do not have permission to edit sensitive events.');
      return;
    }
    if (event) {
      openEventEditor(event);
      return;
    }
    window.alert('Select a safeguarding event to review.');
  };

  const handleApproveJournal = (entry) => {
    if (!student || !entry) return;
    if (entry.status === 'approved') return;
    journalService.updateEntry(entry.id, {
      status: 'approved',
      approvedBy: user?.name || 'Staff',
      approvedAt: new Date().toISOString(),
      pointsAwarded: entry.pointsAwarded || entry.category !== 'wellbeing'
    });
    setJournalEntries(journalService.listForStudent(student.id));

    if (entry.category === 'wellbeing' && !entry.pointsAwarded) {
      const rules = settings.studentPortal?.pointsRules || {};
      const points = Number(rules.wellbeingCheckin || 0);
      if (settings.studentPortal?.pointsEnabled && points > 0) {
        pointsService.awardPoints({
          student,
          points,
          reason: 'Wellbeing check-in approved',
          source: 'wellbeing',
          createdBy: user?.name || 'Staff',
          thresholds: settings.studentPortal?.rewards || [],
          staffRecipients: []
        });
      }
      journalService.updateEntry(entry.id, { pointsAwarded: true });
      setJournalEntries(journalService.listForStudent(student.id));
    }

    externalMessageService.sendFromStaff({
      subject: `Wellbeing check-in approved`,
      body: `${student.name}, your wellbeing check-in has been reviewed and approved.`,
      fromUser: user || { id: 'system', name: 'SafeguardPro' },
      recipients: [{ id: student.id, name: student.name, type: 'student' }],
      category: 'wellbeing'
    });
  };

  const handleSetPortalPassword = async () => {
    setPortalStatus('');
    const trimmedPassword = portalPassword.trim();
    const trimmedConfirm = portalPasswordConfirm.trim();
    if (trimmedPassword.length < 8) {
      setPortalStatus('Password must be at least 8 characters.');
      return;
    }
    if (trimmedPassword !== trimmedConfirm) {
      setPortalStatus('Passwords do not match.');
      return;
    }
    const hash = await buildPasswordHash(trimmedPassword);
    controller.updateStudent(
      student.id,
      {
        portalPasswordHash: hash,
        portalPasswordMustChange: false,
        portalResetTokenHash: '',
        portalResetExpiresAt: null
      },
      user
    );
    setPortalPassword('');
    setPortalPasswordConfirm('');
    setPortalStatus('Portal password updated.');
  };

  const handleSendPortalReset = async () => {
    setPortalStatus('');
    if (!student.email) {
      setPortalStatus('Student email is required to send a reset link.');
      return;
    }
    setPortalSending(true);
    const { token, tokenHash, expiresAt } = await buildResetToken();
    controller.updateStudent(
      student.id,
      {
        portalResetTokenHash: tokenHash,
        portalResetExpiresAt: expiresAt
      },
      user
    );
    const actionUrl = typeof window !== 'undefined'
      ? `${window.location.origin}/student-portal/reset?token=${token}`
      : `/student-portal/reset?token=${token}`;
    const log = emailService.sendTemplate({
      templateKey: 'student.portal_reset',
      to: student.email,
      data: {
        student_name: student.name,
        action_url: actionUrl,
        expires_at: formatDateTime(expiresAt),
        site_name: siteName
      },
      entity: { type: 'student', id: student.id }
    });
    if (log.status === 'failed') {
      setPortalStatus(`Reset email failed: ${log.error_message}`);
    } else {
      setPortalStatus('Reset email sent.');
    }
    setPortalSending(false);
  };

  const showRiskBanner = ['high', 'critical'].includes(student.riskLevel);
  const vulnerabilityCount = student.vulnerabilities.length;
  const vulnerabilityLevel =
    vulnerabilityCount >= 5 ? 'critical' : vulnerabilityCount >= 3 ? 'high' : vulnerabilityCount >= 1 ? 'medium' : 'low';
  const activeSafeguarding =
    student.safeguardingEvents.filter((e) => e.status === 'open').length +
    student.medicalEvents.filter((e) => e.status === 'open').length +
    student.policeEvents.filter((e) => e.status === 'open').length;
  const visibleSafeguardingEvents = (student.safeguardingEvents || []).filter((event) => permissions.canViewEvent(event));
  const visibleMedicalEvents = (student.medicalEvents || []).filter((event) => permissions.canViewEvent(event));
  const visiblePoliceEvents = (student.policeEvents || []).filter((event) => permissions.canViewEvent(event));
  const allSafeguardingEvents = [
    ...visibleSafeguardingEvents,
    ...visibleMedicalEvents,
    ...visiblePoliceEvents
  ];
  const safeguardingLeadName = student.safeguardingLead || student.keyWorker?.name || '';
  const safeguardingSummary = getSafeguardingSummary(allSafeguardingEvents, safeguardingLeadName);
  const actionRequiredEvents = getActionRequiredEvents(allSafeguardingEvents);
  const supportWorkerOptions = [
    ...(user?.name ? [{ name: user.name, role: user.role || user.title || '' }] : []),
    ...(student.supportWorkers || []),
    ...(student.keyWorker ? [student.keyWorker] : []),
    ...staffList
  ].reduce((acc, worker) => {
    if (!worker || !worker.name) return acc;
    if (!acc.find((item) => item.name === worker.name)) {
      acc.push(worker);
    }
    return acc;
  }, []);
  const supportWorkerSelectOptions = supportWorkerOptions.map((worker) => ({
    value: worker.name,
    label: worker.name,
    meta: worker.role || worker.title || ''
  }));

  const incidentReports = incidentController.listByStudent(student.id);
  const assignedInventory = inventoryController.getAssignmentsForStudent(student.id);
  const incidentFollowUps = incidentReports
    .filter((report) => report.followUpDate && report.status !== 'closed')
    .map((report) => {
      const dueDate = new Date(report.followUpDate);
      const today = new Date();
      const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
      const statusBadge =
        diffDays < 0
          ? { label: 'Overdue', variant: 'danger' }
          : diffDays <= 7
          ? { label: 'Due Soon', variant: 'review' }
          : { label: 'Upcoming', variant: 'info' };
      return { ...report, statusBadge };
    });

  const openExportModal = () => {
    const { start, end } = getExportDateDefaults();
    setExportStartDate(start.toISOString().slice(0, 10));
    setExportEndDate(end.toISOString().slice(0, 10));
    setExportOptions(defaultExportOptions);
    setExportError('');
    setExportModalOpen(true);
  };

  const exportFileSuffix = (startValue, endValue) => {
    if (startValue && endValue) return `${startValue}_to_${endValue}`;
    if (startValue) return `from_${startValue}`;
    if (endValue) return `until_${endValue}`;
    return 'all_time';
  };

  const getDocumentDate = (doc) => {
    const versionDates = (doc.versions || []).map((version) => version.uploadedAt).filter(Boolean);
    if (versionDates.length) {
      return versionDates.sort().slice(-1)[0];
    }
    return doc.metadata?.uploadedAt || doc.metadata?.date || '';
  };

  const exportOptionList = [
    { key: 'incidents', label: 'Incident reports' },
    { key: 'attendance', label: 'Attendance history' },
    { key: 'safeguarding', label: 'Safeguarding, medical, and police events' },
    { key: 'notes', label: 'Staff notes' },
    { key: 'reviews', label: 'Support reviews' },
    { key: 'documents', label: 'Documents' },
    { key: 'certificates', label: 'Certificates' },
    { key: 'enrolments', label: 'Programme enrolments' },
    { key: 'activities', label: 'One-off activities' },
    { key: 'inventory', label: 'Inventory assignments' },
    { key: 'statusHistory', label: 'Status history' }
  ];

  const getExportDateDefaults = () => {
    const dates = [];
    const pushDate = (value) => {
      const parsed = parseDateValue(value);
      if (parsed) dates.push(parsed);
    };

    incidentReports.forEach((report) => {
      pushDate(report.date);
      pushDate(report.createdAt);
      pushDate(report.updatedAt);
      pushDate(report.followUpDate);
    });

    (student.attendance?.logs || []).forEach((log) => {
      pushDate(log.date);
      pushDate(log.recordedAt);
    });
    (student.attendance?.absenceReasons || []).forEach((reason) => pushDate(reason.date));

    [...(student.safeguardingEvents || []), ...(student.medicalEvents || []), ...(student.policeEvents || [])]
      .forEach((event) => {
        pushDate(event.date);
        pushDate(event.reviewDate);
        pushDate(event.createdAt);
        pushDate(event.updatedAt);
      });

    (student.notes || []).forEach((note) => pushDate(note.createdAt));
    (student.supportReviews || []).forEach((review) => {
      pushDate(review.dueDate);
      pushDate(review.createdAt);
      pushDate(review.updatedAt);
    });

    (student.documents || []).forEach((doc) => pushDate(getDocumentDate(doc)));
    (student.certificates || []).forEach((cert) => {
      pushDate(cert.issueDate);
      pushDate(cert.expiryDate);
    });
    (student.enrolments || []).forEach((enrolment) => {
      pushDate(enrolment.startDate);
      pushDate(enrolment.endDate);
    });
    (student.activities || []).forEach((activity) => {
      pushDate(activity.date);
    });
    assignedInventory.forEach(({ assignment }) => {
      pushDate(assignment.checkoutDate);
      pushDate(assignment.dueDate);
      pushDate(assignment.expectedReturnDate);
      pushDate(assignment.checkinDate);
    });
    (student.statusHistory || []).forEach((status) => pushDate(status.date));

    if (!dates.length) {
      const today = new Date();
      return { start: today, end: today };
    }
    dates.sort((a, b) => a - b);
    return { start: dates[0], end: dates[dates.length - 1] };
  };

  const handleExportHistory = () => {
    setExportError('');
    if (!exportStartDate || !exportEndDate) {
      setExportError('Select a start and end date for the export.');
      return;
    }
    const range = buildDateRange(exportStartDate, exportEndDate);
    if (range.start && range.end && range.start > range.end) {
      setExportError('Start date must be before the end date.');
      return;
    }
    const selectedKeys = Object.entries(exportOptions).filter(([, value]) => value);
    if (!selectedKeys.length) {
      setExportError('Select at least one section to export.');
      return;
    }

    const suffix = exportFileSuffix(exportStartDate, exportEndDate);
    const studentLabel = (student.reference || student.id || 'student').replace(/\s+/g, '-');

    const workbook = XLSX.utils.book_new();

    if (exportOptions.incidents) {
      const headers = [
        'Incident ID',
        'Type',
        'Date',
        'Severity',
        'Status',
        'Reported By',
        'Assigned Key Worker',
        'Follow Up Required',
        'Follow Up Date',
        'Follow Up Prompt',
        'Participants',
        'Inventory Items',
        'Documents',
        'Description',
        'Created At',
        'Updated At'
      ];
      const rows = incidentReports
        .filter((report) => isWithinRange(report.date, range))
        .map((report) => [
          report.id,
          report.type,
          report.date,
          report.severity,
          report.status,
          report.reportedBy,
          report.assignedKeyWorker,
          report.followUpRequired ? 'Yes' : 'No',
          report.followUpDate || '',
          report.followUpPrompt || '',
          (report.participants || [])
            .map((participant) => `${participant.name}${participant.role ? ` (${participant.role})` : ''}`)
            .join('; '),
          (report.inventoryItemIds || []).map((itemId) => inventoryLookup[itemId] || itemId).join('; '),
          (report.documents || []).map((doc) => doc.name).join('; '),
          report.description,
          report.createdAt || '',
          report.updatedAt || ''
        ]);
      appendSheet(
        workbook,
        'Incidents',
        headers,
        rows,
        'No incident reports in the selected period.'
      );
    }

    if (exportOptions.attendance) {
      const headers = [
        'Type',
        'Date',
        'Status',
        'Programme',
        'Notes/Reason',
        'Recorded By',
        'Recorded At',
        'Attendance Rate',
        'Trend',
        'Scheduled Days'
      ];
      const logs = (student.attendance?.logs || []).filter((log) =>
        isWithinRange(log.date || log.recordedAt, range)
      );
      const absences = (student.attendance?.absenceReasons || []).filter((reason) =>
        isWithinRange(reason.date, range)
      );
      const rows = [
        ...logs.map((log) => [
          'session',
          log.date || log.recordedAt || '',
          log.status || '',
          log.programme || '',
          log.notes || '',
          log.recordedBy || '',
          log.recordedAt || '',
          student.attendance?.rate || 0,
          student.attendance?.trend || '',
          student.attendance?.scheduled || []
        ]),
        ...absences.map((reason) => [
          'absence',
          reason.date || '',
          'absence',
          '',
          reason.reason || '',
          '',
          '',
          student.attendance?.rate || 0,
          student.attendance?.trend || '',
          student.attendance?.scheduled || []
        ])
      ];
      appendSheet(
        workbook,
        'Attendance',
        headers,
        rows,
        'No attendance records in the selected period.'
      );
    }

    if (exportOptions.safeguarding) {
      const headers = [
        'Category',
        'Type',
        'Severity',
        'Status',
        'Date',
        'Assigned Owner',
        'Review Date',
        'Escalation Level',
        'Description',
        'Attachments',
        'Created By',
        'Created At',
        'Updated At'
      ];
      const combinedEvents = [
        ...(student.safeguardingEvents || []).map((event) => ({ ...event, categoryLabel: 'Safeguarding' })),
        ...(student.medicalEvents || []).map((event) => ({ ...event, categoryLabel: 'Medical' })),
        ...(student.policeEvents || []).map((event) => ({ ...event, categoryLabel: 'Police' }))
      ];
      const rows = combinedEvents
        .filter((event) => isWithinRange(event.date, range))
        .map((event) => [
          event.categoryLabel || event.category,
          event.type,
          event.severity,
          event.status,
          event.date,
          event.assignedOwner || '',
          event.reviewDate || '',
          event.escalationLevel || '',
          event.description || '',
          event.attachments || [],
          event.createdBy || '',
          event.createdAt || '',
          event.updatedAt || ''
        ]);
      appendSheet(
        workbook,
        'Safeguarding',
        headers,
        rows,
        'No safeguarding events in the selected period.'
      );
    }

    if (exportOptions.notes) {
      const headers = ['Created At', 'Author', 'Note'];
      const rows = (student.notes || [])
        .filter((note) => isWithinRange(note.createdAt, range))
        .map((note) => [note.createdAt || '', note.createdBy || '', note.content || '']);
      appendSheet(workbook, 'Notes', headers, rows, 'No notes in the selected period.');
    }

    if (exportOptions.reviews) {
      const headers = [
        'Title',
        'Due Date',
        'Status',
        'Assigned To',
        'Notes',
        'Created By',
        'Created At',
        'Updated At'
      ];
      const rows = (student.supportReviews || [])
        .filter((review) => isWithinRange(review.dueDate || review.createdAt, range))
        .map((review) => [
          review.title,
          review.dueDate,
          review.status,
          review.assignedTo || '',
          review.notes || '',
          review.createdBy || '',
          review.createdAt || '',
          review.updatedAt || ''
        ]);
      appendSheet(
        workbook,
        'Support Reviews',
        headers,
        rows,
        'No support reviews in the selected period.'
      );
    }

    if (exportOptions.documents) {
      const headers = ['Document Name', 'Tags', 'Latest Upload Date', 'Latest Upload By', 'Files', 'Visibility', 'Metadata', 'Notes'];
      const rows = (student.documents || [])
        .filter((doc) => {
          const docDate = getDocumentDate(doc);
          return docDate ? isWithinRange(docDate, range) : true;
        })
        .map((doc) => {
          const latestVersion = (doc.versions || [])
            .filter((version) => version.uploadedAt)
            .sort((a, b) => (a.uploadedAt < b.uploadedAt ? 1 : -1))[0];
          return [
            doc.name,
            doc.tags || [],
            latestVersion?.uploadedAt || '',
            latestVersion?.uploadedBy || '',
            (doc.versions || []).map((version) => version.fileName).filter(Boolean),
            doc.visibility || [],
            doc.metadata || {},
            doc.notes || ''
          ];
        });
      appendSheet(
        workbook,
        'Documents',
        headers,
        rows,
        'No documents in the selected period.'
      );
    }

    if (exportOptions.certificates) {
      const headers = ['Certificate', 'Issue Date', 'Expiry Date', 'File'];
      const rows = (student.certificates || [])
        .filter((cert) => isWithinRange(cert.issueDate || cert.expiryDate, range))
        .map((cert) => [cert.name, cert.issueDate, cert.expiryDate || '', cert.fileName || '']);
      appendSheet(
        workbook,
        'Certificates',
        headers,
        rows,
        'No certificates in the selected period.'
      );
    }

    if (exportOptions.enrolments) {
      const headers = ['Programme', 'Start Date', 'End Date', 'Days'];
      const rows = (student.enrolments || [])
        .filter((enrol) => isRangeOverlap(enrol.startDate, enrol.endDate, range))
        .map((enrol) => [enrol.programme, enrol.startDate, enrol.endDate || '', enrol.days || []]);
      appendSheet(
        workbook,
        'Enrolments',
        headers,
        rows,
        'No enrolments in the selected period.'
      );
    }

    if (exportOptions.activities) {
      const headers = ['Activity ID', 'Activity', 'Date', 'Time', 'Location', 'Status', 'Notes'];
      const rows = (student.activities || [])
        .filter((activity) => isWithinRange(activity.date, range))
        .map((activity) => [
          activity.activityId || activity.id || '',
          activity.name || activity.activityName || '',
          activity.date || '',
          activity.time || '',
          activity.location || '',
          activity.status || '',
          activity.notes || ''
        ]);
      appendSheet(
        workbook,
        'Activities',
        headers,
        rows,
        'No activities in the selected period.'
      );
    }

    if (exportOptions.inventory) {
      const headers = [
        'Item',
        'Type',
        'Checkout Date',
        'Due Date',
        'Expected Return',
        'Checkin Date',
        'Status',
        'Notes'
      ];
      const rows = assignedInventory
        .filter(({ assignment }) => isRangeOverlap(assignment.checkoutDate, assignment.checkinDate, range))
        .map(({ item, assignment }) => [
          item.name,
          item.type || '',
          assignment.checkoutDate,
          assignment.dueDate || '',
          assignment.expectedReturnDate || '',
          assignment.checkinDate || '',
          assignment.checkinDate ? 'returned' : 'checked_out',
          assignment.notes || ''
        ]);
      appendSheet(
        workbook,
        'Inventory',
        headers,
        rows,
        'No inventory assignments in the selected period.'
      );
    }

    if (exportOptions.statusHistory) {
      const headers = ['Status', 'Date', 'By'];
      const rows = (student.statusHistory || [])
        .filter((status) => isWithinRange(status.date, range))
        .map((status) => [status.status, status.date, status.by || '']);
      appendSheet(
        workbook,
        'Status History',
        headers,
        rows,
        'No status changes in the selected period.'
      );
    }

    XLSX.writeFile(workbook, `${studentLabel}-history-${suffix}.xlsx`);
    setExportModalOpen(false);
  };

  const openEventModal = (category) => {
    setEditingEventId(null);
    setEventCategory(category);
    setEventForm({
      type: '',
      severity: 'medium',
      status: 'open',
      description: '',
      date: new Date().toISOString().slice(0, 10),
      assignedOwner: user?.name || '',
      reviewDate: '',
      escalationLevel: 'L1',
      attachments: ''
    });
    setEventFiles([]);
    setEventError('');
    setEventModalOpen(true);
  };

  const openEventEditor = (event) => {
    if (!event) return;
    const base = event.toJSON ? event.toJSON() : event;
    setEditingEventId(base.id);
    setEventCategory(base.category || 'Social Care');
    setEventForm({
      type: base.type || '',
      severity: base.severity || 'medium',
      status: base.status || 'open',
      description: base.description || '',
      date: base.date || new Date().toISOString().slice(0, 10),
      assignedOwner: base.assignedOwner || '',
      reviewDate: base.reviewDate || '',
      escalationLevel: base.escalationLevel || 'L1',
      attachments: (base.attachments || []).join(', ')
    });
    setEventFiles([]);
    setEventError('');
    setEventModalOpen(true);
  };

  const closeEventModal = () => {
    setEventModalOpen(false);
    setEditingEventId(null);
  };

  const submitEvent = () => {
    if (!eventForm.type.trim() || !eventForm.description.trim()) {
      setEventError('Type and description are required.');
      return;
    }
    const fileAttachments = eventFiles.map((file) => file.name);
    const textAttachments = eventForm.attachments
      ? eventForm.attachments.split(',').map((item) => item.trim()).filter(Boolean)
      : [];
    const payload = {
      category: eventCategory,
      type: eventForm.type.trim(),
      severity: eventForm.severity,
      status: eventForm.status,
      description: eventForm.description.trim(),
      date: eventForm.date,
      assignedOwner: eventForm.assignedOwner,
      reviewDate: eventForm.reviewDate || null,
      escalationLevel: eventForm.escalationLevel,
      attachments: [...textAttachments, ...fileAttachments],
      createdBy: user?.name || 'System'
    };
    if (editingEventId) {
      const updateList = (events) =>
        (events || []).map((event) => {
          const base = event.toJSON ? event.toJSON() : event;
          if (base.id !== editingEventId) return base;
          return { ...base, ...payload, updatedAt: new Date().toISOString() };
        });
      if (eventCategory === 'Medical') {
        controller.updateStudent(
          student.id,
          { medicalEvents: updateList(student.medicalEvents), lastUpdated: new Date().toISOString() },
          user
        );
      } else if (eventCategory === 'Police') {
        controller.updateStudent(
          student.id,
          { policeEvents: updateList(student.policeEvents), lastUpdated: new Date().toISOString() },
          user
        );
      } else {
        controller.updateStudent(
          student.id,
          { safeguardingEvents: updateList(student.safeguardingEvents), lastUpdated: new Date().toISOString() },
          user
        );
      }
    } else if (eventCategory === 'Medical') {
      controller.addMedicalEvent(student.id, payload, user);
    } else if (eventCategory === 'Police') {
      controller.addPoliceEvent(student.id, payload, user);
    } else {
      controller.addSafeguardingEvent(student.id, payload, user);
    }
    setEditingEventId(null);
    setEventModalOpen(false);
  };

  const openReviewModal = () => {
    setReviewForm({
      title: '',
      dueDate: new Date().toISOString().slice(0, 10),
      assignedTo: user?.name || '',
      status: 'open',
      notes: ''
    });
    setReviewError('');
    setReviewModalOpen(true);
  };

  const submitReview = () => {
    if (!reviewForm.title.trim() || !reviewForm.dueDate) {
      setReviewError('Title and due date are required.');
      return;
    }
    controller.addSupportReview(
      student.id,
      {
        title: reviewForm.title.trim(),
        dueDate: reviewForm.dueDate,
        assignedTo: reviewForm.assignedTo,
        status: reviewForm.status,
        notes: reviewForm.notes
      },
      user
    );
    setReviewModalOpen(false);
  };

  const openAttendanceModal = () => {
    setAttendanceForm({
      date: new Date().toISOString().slice(0, 10),
      status: 'attended',
      programme: '',
      notes: ''
    });
    setAttendanceModalOpen(true);
  };

  const openIncidentModal = () => {
    setIncidentForm({
      type: 'Behavioural',
      description: '',
      date: new Date().toISOString().slice(0, 16),
      severity: 'medium',
      status: 'open',
      assignedKeyWorker: user?.name || '',
      followUpRequired: true,
      followUpPrompt: '',
      followUpDate: ''
    });
    setIncidentFiles([]);
    setIncidentInventorySelection([]);
    setIncidentStaffSelection([]);
    setIncidentError('');
    setIncidentModalOpen(true);
  };

  const toggleIncidentInventory = (itemId) => {
    setIncidentInventorySelection((prev) =>
      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]
    );
  };

  const toggleIncidentStaff = (staffId) => {
    setIncidentStaffSelection((prev) =>
      prev.includes(staffId) ? prev.filter((id) => id !== staffId) : [...prev, staffId]
    );
  };

  const handleAddIncidentInventoryItem = () => {
    if (!newIncidentInventoryItem.name.trim()) return;
    const item = inventoryController.createItem(
      {
        name: newIncidentInventoryItem.name.trim(),
        type: newIncidentInventoryItem.type || 'Other',
        condition: newIncidentInventoryItem.condition || 'Good',
        status: 'available'
      },
      user
    );
    setIncidentInventorySelection((prev) => [item.id, ...prev]);
    setNewIncidentInventoryItem({ name: '', type: 'Tech Equipment', condition: 'Good' });
    setShowIncidentInventoryModal(false);
  };

  const handleIncidentSubmit = async () => {
    if (!incidentForm.description.trim()) {
      setIncidentError('Description is required.');
      return;
    }
    const documents = await readFilesAsStoredAttachments(incidentFiles, user?.name || 'System');
    const participants = [
      { id: student.id, type: 'student', name: student.name, role: 'Student' },
      ...incidentStaffSelection.map((staffId) => {
        const staff = staffList.find((member) => member.id === staffId);
        return { id: staffId, type: 'staff', name: staff?.name || '', role: staff?.role || 'Support Worker' };
      })
    ];
    incidentController.createIncident(
      {
        type: incidentForm.type,
        date: incidentForm.date,
        severity: incidentForm.severity,
        status: incidentForm.status,
        reportedBy: user?.name || 'System',
        description: incidentForm.description.trim(),
        assignedKeyWorker: incidentForm.assignedKeyWorker || '',
        followUpRequired: incidentForm.followUpRequired,
        followUpDate: incidentForm.followUpRequired ? incidentForm.followUpDate : null,
        followUpPrompt: incidentForm.followUpRequired ? incidentForm.followUpPrompt : '',
        participants,
        inventoryItemIds: incidentInventorySelection,
        documents
      },
      user
    );
    setIncidentForm({
      type: 'Behavioural',
      description: '',
      date: new Date().toISOString().slice(0, 16),
      severity: 'medium',
      status: 'open',
      assignedKeyWorker: user?.name || '',
      followUpRequired: true,
      followUpPrompt: '',
      followUpDate: ''
    });
    setIncidentFiles([]);
    setIncidentInventorySelection([]);
    setIncidentStaffSelection([]);
    setIncidentError('');
    setIncidentModalOpen(false);
  };

  const submitAttendanceLog = () => {
    const log = {
      id: `attlog-${Math.random().toString(36).slice(2, 9)}`,
      date: attendanceForm.date,
      status: attendanceForm.status,
      programme: attendanceForm.programme,
      notes: attendanceForm.notes,
      recordedBy: user?.name || 'System'
    };
    const current = student.attendance?.toJSON ? student.attendance.toJSON() : student.attendance || {};
    const logs = [log, ...(current.logs || [])];
    controller.updateStudent(
      student.id,
      { attendance: { ...current, logs } },
      user
    );
    setAttendanceModalOpen(false);
  };

  const handleForward = () => {
    const payload = {
      subject: `Student info: ${student.name}`,
      body: [
        `Student: ${student.name}`,
        `Risk level: ${student.riskLevel || 'N/A'}`,
        `Status: ${student.status || 'N/A'}`,
        student.keyWorker?.name ? `Key worker: ${student.keyWorker.name}` : 'Key worker: Unassigned'
      ].join('\n'),
      references: [
        {
          type: 'student',
          id: student.id,
          label: `${student.name} (${student.reference || student.id})`,
          route: `/students/${student.id}/dashboard`
        }
      ]
    };
    navigate('/messages', { state: { compose: payload } });
  };

  return (
    <div className="space-y-6">
      {showRiskBanner && (
        <div className={`sp-alert ${student.riskLevel === 'critical' ? 'sp-alert--critical' : 'sp-alert--high'} flex items-center justify-between flex-wrap gap-4`}>
          <div>
            <p className="sp-value" style={{ fontWeight: 600 }}>
              {student.riskLevel === 'critical' ? 'Critical Risk' : 'High Risk'} student - immediate safeguarding review required.
            </p>
            <p className="text-sm sp-muted">Ensure review date is set and assigned owner is notified.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={goToSafeguardingTab}>
            Review safeguards
          </Button>
        </div>
      )}

      <StudentHeader
        student={student}
        onBack={() => navigate('/students')}
        onEdit={() => navigate(`/students/${student.id}/edit`)}
        onExport={openExportModal}
        onForward={handleForward}
      />

      <StudentTabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      <TabPanel id="overview" active={activeTab} idBase="student-profile">
        <div className="grid lg:grid-cols-3 gap-4">
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Key Worker</h3>
            <p className="text-sm sp-muted mt-2">{student.keyWorker?.name || 'Unassigned'}</p>
            <p className="text-xs sp-muted mt-2">{student.keyWorker?.notes || 'No notes yet.'}</p>
          </Card>
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Attendance</h3>
            <p className="sp-value" style={{ fontSize: '22px', fontWeight: 600, marginTop: '8px' }}>{student.attendance.rate}%</p>
            <p className="text-xs sp-muted">Trend: {student.attendance.trend}</p>
          </Card>
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Active Safeguarding</h3>
            <p className="sp-value" style={{ fontSize: '22px', fontWeight: 600, marginTop: '8px' }}>
              {student.safeguardingEvents.filter((e) => e.status === 'open').length}
            </p>
            <p className="text-xs sp-muted">Open events</p>
          </Card>
        </div>
        <Card className="p-5 mt-4">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Vulnerabilities</h3>
          <div className="flex flex-wrap gap-2 mt-3">
            {student.vulnerabilities.length ? (
              student.vulnerabilities.map((v) => (
                <Badge key={v} variant="info" className="sp-badge--subtle">
                  {v.replace('_', ' ')}
                </Badge>
              ))
            ) : (
              <span className="text-sm sp-muted">No vulnerabilities flagged.</span>
            )}
          </div>
        </Card>
        <Card className="p-5 mt-4">
          <h3 className="sp-value" style={{ fontWeight: 600 }}>Assigned Inventory</h3>
          <div className="mt-3 space-y-2">
            {assignedInventory.length === 0 && (
              <p className="text-sm sp-muted">No items assigned.</p>
            )}
            {assignedInventory.map(({ item, assignment, due }) => (
              <div key={`${item.id}-${assignment.id}`} className="sp-panel sp-panel--muted p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm" style={{ fontWeight: 600 }}>{item.name}</p>
                    <p className="text-xs sp-muted">Due {formatDate(assignment.dueDate) || 'N/A'}</p>
                  </div>
                  {due.isOverdue && <Badge variant="danger">Overdue</Badge>}
                  {!due.isOverdue && due.isDueSoon && <Badge variant="review">Due soon</Badge>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="info" active={activeTab} idBase="student-profile">
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Basic Info</h3>
            <div className="mt-3 text-sm sp-muted space-y-2">
              <p><span className="sp-value" style={{ fontWeight: 600 }}>Student ID:</span> {student.reference || student.id}</p>
              <p><span className="sp-value" style={{ fontWeight: 600 }}>DOB:</span> {student.dob || 'Not set'}</p>
              <p><span className="sp-value" style={{ fontWeight: 600 }}>School:</span> {student.school || 'Not set'}</p>
              <p><span className="sp-value" style={{ fontWeight: 600 }}>Address:</span> {student.address?.join(', ') || 'Not set'}</p>
              <p><span className="sp-value" style={{ fontWeight: 600 }}>Local Authority:</span> {student.localAuthority || 'None'}</p>
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Contacts</h3>
            <div className="mt-3 space-y-3">
              {student.contacts.map((contact) => (
                <div key={contact.id} className="sp-panel sp-panel--muted p-3">
                  <p className="text-sm" style={{ fontWeight: 600 }}>{contact.name}</p>
                  <p className="text-xs sp-muted">{contact.role}</p>
                  <p className="text-xs sp-muted">{contact.phone} - {contact.email}</p>
                </div>
              ))}
              {student.contacts.length === 0 && (
                <p className="text-sm sp-muted">No contacts recorded.</p>
              )}
            </div>
          </Card>
          <Card className="p-5">
            <h3 className="sp-value" style={{ fontWeight: 600 }}>Student Portal Login</h3>
            <div className="mt-3 space-y-3 text-sm">
              <div className="sp-panel sp-panel--muted p-3">
                <div className="sp-label">Portal email</div>
                <div className="sp-value">{student.email || 'No email on file'}</div>
                <div className="text-xs sp-muted mt-1">
                  Email is linked to the student record. Update it in Student Info if needed.
                </div>
              </div>
              <div className="sp-panel sp-panel--muted p-3">
                <div className="sp-label">Password status</div>
                <div className="sp-value">{student.portalPasswordHash ? 'Password set' : 'No password set'}</div>
                {student.portalResetExpiresAt && (
                  <div className="text-xs sp-muted mt-1">
                    Reset link expires {formatDateTime(student.portalResetExpiresAt)}.
                  </div>
                )}
              </div>
              <div>
                <label className="sp-label">Set portal password</label>
                <input
                  className="sp-input mt-2 w-full"
                  type="password"
                  value={portalPassword}
                  onChange={(event) => setPortalPassword(event.target.value)}
                  placeholder="New password (min 8 chars)"
                />
              </div>
              <div>
                <label className="sp-label">Confirm password</label>
                <input
                  className="sp-input mt-2 w-full"
                  type="password"
                  value={portalPasswordConfirm}
                  onChange={(event) => setPortalPasswordConfirm(event.target.value)}
                  placeholder="Confirm new password"
                />
              </div>
              {portalStatus && <div className="text-xs sp-warning-text">{portalStatus}</div>}
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleSetPortalPassword}>
                  Save portal password
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleSendPortalReset}
                  disabled={portalSending}
                >
                  {portalSending ? 'Sending reset…' : 'Send reset email'}
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </TabPanel>

      <TabPanel id="safeguarding" active={activeTab} idBase="student-profile">
        <div className="space-y-4">
          <SafeguardingSummary summary={safeguardingSummary} />
          <ActionRequiredList
            events={actionRequiredEvents}
            onReviewEvent={handleSensitiveEdit}
            onAssignOwner={handleSensitiveEdit}
            onSetReviewDate={handleSensitiveEdit}
          />
          <div className="grid lg:grid-cols-3 gap-4">
            <Card className="p-5 sp-panel--muted">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Vulnerability Level</h3>
              <div className="mt-3 flex items-center gap-2">
                <Badge variant={vulnerabilityLevel}>{vulnerabilityLevel}</Badge>
                <span className="text-xs sp-muted">{vulnerabilityCount} vulnerabilities recorded</span>
              </div>
            </Card>
            <Card className="p-5 sp-panel--muted">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Active Events</h3>
              <p className="sp-value" style={{ fontSize: '22px', fontWeight: 600, marginTop: '8px' }}>{activeSafeguarding}</p>
              <p className="text-xs sp-muted">Open safeguarding, medical, and police events</p>
            </Card>
            <Card className="p-5 sp-panel--muted">
              <h3 className="sp-value" style={{ fontWeight: 600 }}>Vulnerabilities</h3>
              <div className="mt-3 flex flex-wrap gap-2">
                {student.vulnerabilities.length ? (
                  student.vulnerabilities.map((v) => (
                    <Badge key={v} variant="info" className="sp-badge--subtle">
                      {v.replace('_', ' ')}
                    </Badge>
                  ))
                ) : (
                  <span className="text-sm sp-muted">No vulnerabilities flagged.</span>
                )}
              </div>
            </Card>
          </div>
          <SafeguardingList
            title="Social Care Events"
            events={visibleSafeguardingEvents}
            permissions={permissions}
            onAddEvent={() => openEventModal('Social Care')}
            onSensitiveEdit={handleSensitiveEdit}
          />
          <SafeguardingList
            title="Medical Events"
            events={visibleMedicalEvents}
            permissions={permissions}
            onAddEvent={() => openEventModal('Medical')}
            onSensitiveEdit={handleSensitiveEdit}
          />
          <SafeguardingList
            title="Police Events"
            events={visiblePoliceEvents}
            permissions={permissions}
            onAddEvent={() => openEventModal('Police')}
            onSensitiveEdit={handleSensitiveEdit}
          />
          <Card className="p-5">
            <div className="sp-section-header sp-section-header--surface flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Support Reviews</h3>
                <p className="text-sm sp-muted">Upcoming and overdue review dates.</p>
              </div>
              <Button size="sm" variant="secondary" onClick={openReviewModal} type="button">
                Add Review
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {(student.supportReviews || []).length === 0 && (
                <p className="text-sm sp-muted">No support reviews scheduled.</p>
              )}
              {(student.supportReviews || []).map((review) => {
                const dueDate = new Date(review.dueDate);
                const today = new Date();
                const diffDays = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));
                const statusBadge =
                  diffDays < 0
                    ? { label: 'Overdue', variant: 'danger' }
                    : diffDays <= 7
                    ? { label: 'Due Soon', variant: 'review' }
                    : { label: 'Upcoming', variant: 'info' };
                return (
                  <div key={review.id} className="sp-panel sp-panel--muted p-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>{review.title}</div>
                        <div className="text-xs sp-muted mt-1">
                          Due: {formatDate(review.dueDate)} · Assigned to: {review.assignedTo || 'Unassigned'}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={statusBadge.variant}>{statusBadge.label}</Badge>
                        <Badge variant={review.status === 'open' ? 'active' : 'inactive'}>{review.status}</Badge>
                      </div>
                    </div>
                    {review.notes && (
                      <div className="text-xs sp-muted mt-2">{review.notes}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          <Card className="p-5">
            <div className="sp-section-header sp-section-header--surface flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Incident Follow-ups</h3>
                <p className="text-sm sp-muted">Follow-up expiry dates that impact safeguarding.</p>
              </div>
              <Button
                size="sm"
                variant="secondary"
                onClick={openIncidentModal}
                type="button"
              >
                Log incident
              </Button>
            </div>
            <div className="mt-4 space-y-3">
              {incidentFollowUps.length === 0 && (
                <p className="text-sm sp-muted">No incident follow-ups scheduled.</p>
              )}
              {incidentFollowUps.map((report) => (
                <div key={report.id} className="sp-panel sp-panel--muted p-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <div className="sp-value" style={{ fontWeight: 600 }}>{report.type}</div>
                      <div className="text-xs sp-muted mt-1">
                        Follow-up due {formatDate(report.followUpDate)} · Assigned to {report.assignedKeyWorker || 'Unassigned'}
                      </div>
                    </div>
                    <Badge variant={report.statusBadge.variant}>{report.statusBadge.label}</Badge>
                  </div>
                  {report.followUpPrompt && (
                    <div className="text-xs sp-muted mt-2">{report.followUpPrompt}</div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </div>
      </TabPanel>

      <TabPanel id="timeline" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <TimelineView events={timelineEvents} />
        </Card>
      </TabPanel>

      <TabPanel id="attendance" active={activeTab} idBase="student-profile">
        <StudentAttendanceView
          student={student}
          attendanceThreshold={attendanceThreshold}
          incidentReports={incidentReports}
          onMarkAttendance={openAttendanceModal}
          onViewIncidents={() => navigate('/incidents')}
        />
      </TabPanel>

      <TabPanel id="documents" active={activeTab} idBase="student-profile">
        <DocumentsPanel
          documents={student.documents}
          onUpload={() => navigate(`/students/${student.id}/documents/new`)}
          onRename={(documentId, name) => controller.updateDocument(student.id, documentId, { name }, user)}
          onUpdate={(documentId, updates) => controller.updateDocument(student.id, documentId, updates, user)}
          onArchive={(documentId) => controller.archiveDocument(student.id, documentId, user)}
          onRestore={(documentId) => controller.restoreDocument(student.id, documentId, user)}
          onDelete={user?.accessScope === 'admin' ? (documentId) => controller.deleteDocument(student.id, documentId, user) : undefined}
          canDelete={user?.accessScope === 'admin'}
        />
      </TabPanel>

      <TabPanel id="certificates" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Certificates</h3>
            <div className="flex items-center gap-2">
              {student.certificates.some((cert) => cert.archived) && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowArchivedCertificates((prev) => !prev)}
                >
                  {showArchivedCertificates ? 'Hide archived' : 'Show archived'}
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => navigate(`/students/${student.id}/certificates/new`)}
              >
                Add Certificate
              </Button>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {student.certificates.filter((cert) => (showArchivedCertificates ? true : !cert.archived)).length ? (
              student.certificates
                .filter((cert) => (showArchivedCertificates ? true : !cert.archived))
                .map((cert) => {
                  const isEditing = editingCertificateId === cert.id;
                  return (
                    <div key={cert.id} className="sp-panel p-4">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input
                                className="sp-input"
                                value={certificateDraftName}
                                onChange={(event) => setCertificateDraftName(event.target.value)}
                                placeholder="Certificate name"
                              />
                              <Button
                                size="sm"
                                variant="secondary"
                                type="button"
                                onClick={() => {
                                  const nextName = certificateDraftName.trim();
                                  if (!nextName) return;
                                  controller.updateCertificate(student.id, cert.id, { name: nextName }, user);
                                  setEditingCertificateId(null);
                                  setCertificateDraftName('');
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                onClick={() => {
                                  setEditingCertificateId(null);
                                  setCertificateDraftName('');
                                }}
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="sp-value" style={{ fontWeight: 600 }}>{cert.name}</p>
                              {cert.archived && <Badge variant="inactive">Archived</Badge>}
                            </div>
                          )}
                          <p className="text-xs sp-muted">Issued: {cert.issueDate}</p>
                          <p className="text-xs sp-muted">Expiry: {cert.expiryDate || 'No expiry'}</p>
                        </div>
                        {!isEditing && (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              type="button"
                              onClick={() => {
                                setEditingCertificateId(cert.id);
                                setCertificateDraftName(cert.name);
                              }}
                            >
                              Rename
                            </Button>
                            {!cert.archived ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                onClick={() => controller.archiveCertificate(student.id, cert.id, user)}
                              >
                                Archive
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="ghost"
                                type="button"
                                onClick={() => controller.restoreCertificate(student.id, cert.id, user)}
                              >
                                Restore
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              type="button"
                              onClick={() => {
                                if (window.confirm('Delete this certificate? This cannot be undone.')) {
                                  controller.deleteCertificate(student.id, cert.id, user);
                                }
                              }}
                            >
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
            ) : (
              <p className="text-sm sp-muted">No certificates recorded.</p>
            )}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="incidents" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Incident Reports</h3>
              <p className="text-sm sp-muted">Operational incidents linked to this student.</p>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={openIncidentModal}
              type="button"
            >
              Log incident
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {incidentReports.length === 0 && (
              <p className="text-sm sp-muted">No incident reports logged yet.</p>
            )}
            {incidentReports.map((report) => (
              <div key={report.id} className="sp-panel sp-panel--muted p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="sp-value" style={{ fontWeight: 600 }}>{report.type}</div>
                    <div className="text-xs sp-muted mt-1">
                      {formatDateTime(report.date)} · Assigned to {report.assignedKeyWorker || 'Unassigned'}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={report.severity || 'medium'}>{report.severity || 'medium'}</Badge>
                    <Badge variant={report.status === 'open' ? 'review' : 'inactive'}>{report.status}</Badge>
                  </div>
                </div>
                {report.description && (
                  <div className="text-xs sp-muted mt-2">{report.description}</div>
                )}
                {report.followUpPrompt && (
                  <div className="text-xs sp-muted mt-2">Follow-up: {report.followUpPrompt}</div>
                )}
                {report.followUpDate && (
                  <div className="text-xs sp-muted mt-1">Follow-up due {formatDate(report.followUpDate)}</div>
                )}
                {(report.inventoryItemIds || []).length > 0 && (
                  <div className="text-xs sp-muted mt-2">
                    Inventory: {report.inventoryItemIds
                      .map((itemId) => inventoryItems.find((item) => item.id === itemId)?.name || itemId)
                      .join(', ')}
                  </div>
                )}
                {(report.documents || []).length > 0 && (
                  <div className="text-xs sp-muted mt-2">
                    Files: {report.documents.map((doc) => doc.name).join(', ')}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="enrolment" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
              Programmes & Activities
            </h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate(`/students/${student.id}/edit#programmes`)}
            >
              Edit Programmes & Activities
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {student.enrolments.length ? (
              student.enrolments.map((enrol) => (
                <div key={enrol.id} className="sp-panel p-4">
                  <p className="sp-value" style={{ fontWeight: 600 }}>{enrol.programme}</p>
                  <p className="text-xs sp-muted">Start: {formatDate(enrol.startDate) || 'TBD'}</p>
                  <p className="text-xs sp-muted">End: {formatDate(enrol.endDate) || 'Ongoing'}</p>
                  <p className="text-xs sp-muted">
                    Days: {(enrol.days && enrol.days.length ? enrol.days : student.attendance?.scheduled || []).join(', ') || 'Not set'}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm sp-muted">No programmes recorded.</p>
            )}
          </div>
        </Card>

        <Card className="p-5 mt-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
              One‑off Activities
            </h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate('/activities/new')}
            >
              Add Activity
            </Button>
          </div>
          <div className="mt-4 space-y-3">
            {(student.activities || []).length ? (
              (student.activities || []).map((activity) => (
                <div key={activity.id} className="sp-panel p-4">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="sp-value" style={{ fontWeight: 600 }}>{activity.name}</p>
                      <p className="text-xs sp-muted">
                        {activity.date ? formatDate(activity.date) : 'TBD'} {activity.time ? `· ${activity.time}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={activity.status === 'cancelled' ? 'danger' : activity.status === 'completed' ? 'inactive' : 'active'}>
                        {activity.status || 'scheduled'}
                      </Badge>
                      {activity.activityId && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => navigate(`/activities/${activity.activityId}/edit`)}
                        >
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm sp-muted">No activities recorded.</p>
            )}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="journal" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Journal entries</h3>
              <p className="text-sm sp-muted">Wellbeing check-ins and student notes.</p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {journalEntries.length === 0 ? (
              <p className="text-sm sp-muted">No journal entries recorded.</p>
            ) : (
              journalEntries
                .slice()
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map((entry) => (
                  <div key={entry.id} className="sp-panel p-4 space-y-2">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <div className="sp-value" style={{ fontWeight: 600 }}>
                          {entry.category?.replace('-', ' ') || 'Journal entry'}
                        </div>
                        <div className="text-xs sp-muted">{formatDateTime(entry.createdAt)}</div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {entry.status === 'pending' && <Badge variant="review">Pending approval</Badge>}
                        {entry.status === 'approved' && <Badge variant="success">Approved</Badge>}
                        {entry.status === 'shared' && <Badge variant="active">Shared</Badge>}
                      </div>
                    </div>
                    {entry.title && <div className="text-sm">{entry.title}</div>}
                    {entry.body && <div className="text-sm sp-muted">{entry.body}</div>}
                    {entry.attachments?.length > 0 && (
                      <div className="text-xs sp-muted">Attachments: {entry.attachments.map((file) => file.name).join(', ')}</div>
                    )}
                    {entry.status === 'pending' && entry.category === 'wellbeing' && (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="secondary" onClick={() => handleApproveJournal(entry)}>
                          Approve check-in & award points
                        </Button>
                      </div>
                    )}
                    {entry.approvedAt && (
                      <div className="text-xs sp-muted">
                        Approved by {entry.approvedBy || 'Staff'} on {formatDateTime(entry.approvedAt)}
                      </div>
                    )}
                  </div>
                ))
            )}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="notes" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Internal Notes</h3>
          </div>
          <div className="mt-4 space-y-3">
            <textarea
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
              placeholder="Add a note for safeguarding context..."
              className="sp-textarea w-full"
            />
            <div className="flex items-center justify-end">
              <Button
                size="sm"
                onClick={() => {
                  const trimmed = noteText.trim();
                  if (!trimmed) return;
                  controller.addNote(student.id, trimmed, user);
                  setNoteText('');
                }}
              >
                Add Note
              </Button>
            </div>
          </div>
          <div className="mt-6 space-y-3">
            {student.notesSorted.length ? (
              student.notesSorted.map((note) => (
                <div key={note.id} className="sp-panel p-4">
                  <p className="text-sm sp-value whitespace-pre-wrap">{note.content}</p>
                  <p className="text-xs sp-muted mt-2">
                    {note.createdBy} - {formatDateTime(note.createdAt)}
                  </p>
                </div>
              ))
            ) : (
              <p className="text-sm sp-muted">No notes yet.</p>
            )}
          </div>
        </Card>
      </TabPanel>

      <TabPanel id="audit" active={activeTab} idBase="student-profile">
        <Card className="p-5">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Audit Log</h3>
            <Button size="sm" variant="secondary" disabled={!permissions.canViewAudit()}>
              Export Log
            </Button>
          </div>
          {permissions.canViewAudit() ? (
            <div className="mt-4 space-y-3">
              {controller.getAuditLogs(student.id).map((log) => (
                <div key={log.id} className="sp-panel p-4">
                  <p className="text-sm" style={{ fontWeight: 600 }}>{log.action}</p>
                  <p className="text-xs sp-muted">
                    {log.user} ({log.role}) - {formatDateTime(log.timestamp)}
                  </p>
                  <pre className="text-xs sp-muted mt-2 whitespace-pre-wrap">
                    {JSON.stringify(log.changes, null, 2)}
                  </pre>
                </div>
              ))}
              {controller.getAuditLogs(student.id).length === 0 && (
                <p className="text-sm sp-muted">No audit entries yet.</p>
              )}
            </div>
          ) : (
            <p className="text-sm sp-muted mt-3">Audit log is restricted.</p>
          )}
        </Card>
      </TabPanel>

      <Modal isOpen={exportModalOpen} onClose={() => setExportModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
                Export student history
              </h3>
              <p className="text-xs sp-muted mt-1">Choose a date range and the sections to export.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExportModalOpen(false)} type="button">
              Close
            </Button>
          </div>
          {exportError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {exportError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">From</label>
              <DateSelect
                value={exportStartDate}
                onChange={setExportStartDate}
                className="mt-2 w-full"
                allowClear={false}
              />
            </div>
            <div>
              <label className="sp-label">To</label>
              <DateSelect
                value={exportEndDate}
                onChange={setExportEndDate}
                className="mt-2 w-full"
                allowClear={false}
              />
            </div>
          </div>
          <div className="sp-panel sp-panel--muted p-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <p className="text-sm sp-value" style={{ fontWeight: 600 }}>Include in export</p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setExportOptions((prev) =>
                    Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: true }), {})
                  )}
                >
                  Select all
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setExportOptions((prev) =>
                    Object.keys(prev).reduce((acc, key) => ({ ...acc, [key]: false }), {})
                  )}
                >
                  Clear all
                </Button>
              </div>
            </div>
            <div className="mt-3 grid md:grid-cols-2 gap-2">
              {exportOptionList.map((option) => (
                <label key={option.key} className="flex items-center gap-2 text-sm sp-muted">
                  <input
                    type="checkbox"
                    checked={Boolean(exportOptions[option.key])}
                    onChange={() =>
                      setExportOptions((prev) => ({ ...prev, [option.key]: !prev[option.key] }))
                    }
                  />
                  {option.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setExportModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleExportHistory} type="button">
              Export
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={eventModalOpen} onClose={closeEventModal}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
                {editingEventId ? `Update ${eventCategory} Event` : `Add ${eventCategory} Event`}
              </h3>
              <p className="text-xs sp-muted mt-1">
                {editingEventId ? 'Update the event details and review status.' : 'Log a new safeguarding event.'}
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={closeEventModal} type="button">
              Close
            </Button>
          </div>
          {eventError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {eventError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Type</label>
              <input
                value={eventForm.type}
                onChange={(e) => setEventForm({ ...eventForm, type: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="e.g. Disclosure"
              />
            </div>
            <div>
              <label className="sp-label">Date</label>
              <DateSelect
                value={eventForm.date}
                onChange={(value) => setEventForm({ ...eventForm, date: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                value={eventForm.severity}
                onChange={(e) => setEventForm({ ...eventForm, severity: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={eventForm.status}
                onChange={(e) => setEventForm({ ...eventForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="open">Open</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Follow-up support worker</label>
              <SearchableSelect
                value={eventForm.assignedOwner}
                options={[{ value: '', label: 'Unassigned' }, ...supportWorkerSelectOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search support worker"
                className="mt-2"
                onChange={(value) => setEventForm({ ...eventForm, assignedOwner: value })}
              />
            </div>
            <div>
              <label className="sp-label">Assigned owner (manual)</label>
              <input
                value={eventForm.assignedOwner}
                onChange={(e) => setEventForm({ ...eventForm, assignedOwner: e.target.value })}
                className="sp-input mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Review / follow-up date</label>
              <DateSelect
                value={eventForm.reviewDate}
                onChange={(value) => setEventForm({ ...eventForm, reviewDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Escalation level</label>
              <select
                value={eventForm.escalationLevel}
                onChange={(e) => setEventForm({ ...eventForm, escalationLevel: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="L1">L1</option>
                <option value="L2">L2</option>
                <option value="L3">L3</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Description</label>
              <textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                rows={4}
                className="sp-textarea mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attachments (comma-separated)</label>
              <input
                value={eventForm.attachments}
                onChange={(e) => setEventForm({ ...eventForm, attachments: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="e.g. case-note.pdf, referral.docx"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attach files</label>
              <input
                type="file"
                multiple
                className="sp-input mt-2 w-full"
                onChange={(e) => setEventFiles(Array.from(e.target.files || []))}
              />
              {eventFiles.length > 0 && (
                <div className="text-xs sp-muted mt-2">
                  {eventFiles.map((file) => file.name).join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={closeEventModal} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={submitEvent}>
              {editingEventId ? 'Save Changes' : 'Save Event'}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={incidentModalOpen} onClose={() => setIncidentModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
                Log Incident Report
              </h3>
              <p className="text-xs sp-muted mt-1">Student: {student.name}</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setIncidentModalOpen(false)} type="button">
              Close
            </Button>
          </div>
          {incidentError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {incidentError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Type</label>
              <select
                value={incidentForm.type}
                onChange={(e) => setIncidentForm({ ...incidentForm, type: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                {incidentTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="sp-label">Date & time</label>
              <input
                type="datetime-local"
                value={incidentForm.date}
                onChange={(e) => setIncidentForm({ ...incidentForm, date: e.target.value })}
                className="sp-input mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Severity</label>
              <select
                value={incidentForm.severity}
                onChange={(e) => setIncidentForm({ ...incidentForm, severity: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={incidentForm.status}
                onChange={(e) => setIncidentForm({ ...incidentForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="open">Open</option>
                <option value="monitoring">Monitoring</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Assigned key worker</label>
              <SearchableSelect
                value={incidentForm.assignedKeyWorker}
                options={[{ value: '', label: 'Unassigned' }, ...supportWorkerSelectOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search key worker"
                className="mt-2"
                onChange={(value) => setIncidentForm({ ...incidentForm, assignedKeyWorker: value })}
              />
            </div>
            <div>
              <label className="sp-label">Follow-up required</label>
              <label className="flex items-center gap-2 text-sm mt-2">
                <input
                  type="checkbox"
                  checked={incidentForm.followUpRequired}
                  onChange={(e) => setIncidentForm({ ...incidentForm, followUpRequired: e.target.checked })}
                />
                Enable follow-up tracking
              </label>
            </div>
            <div>
              <label className="sp-label">Follow-up due</label>
              <DateSelect
                value={incidentForm.followUpDate}
                onChange={(value) =>
                  setIncidentForm({ ...incidentForm, followUpDate: value, followUpRequired: true })
                }
                className="mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Description</label>
              <textarea
                value={incidentForm.description}
                onChange={(e) => setIncidentForm({ ...incidentForm, description: e.target.value })}
                rows={4}
                className="sp-textarea mt-2 w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Follow-up prompt</label>
              <textarea
                value={incidentForm.followUpPrompt}
                onChange={(e) => setIncidentForm({ ...incidentForm, followUpPrompt: e.target.value })}
                rows={3}
                className="sp-textarea mt-2 w-full"
                placeholder="Next steps / follow-up instructions"
              />
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Support workers involved</label>
              <SearchableChecklist
                className="mt-2"
                items={staffList}
                selectedIds={incidentStaffSelection}
                onToggle={toggleIncidentStaff}
                placeholder="Search staff"
                getLabel={(worker) => worker.name}
                getMeta={(worker) => worker.role || worker.title || ''}
                emptyLabel="No staff found."
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <label className="sp-label">Related inventory</label>
                <Button size="sm" variant="ghost" type="button" onClick={() => setShowIncidentInventoryModal(true)}>
                  Add item
                </Button>
              </div>
              {inventoryItems.length > 0 ? (
                <SearchableChecklist
                  className="mt-2"
                  items={inventoryItems}
                  selectedIds={incidentInventorySelection}
                  onToggle={toggleIncidentInventory}
                  placeholder="Search inventory"
                  getLabel={(item) => item.name}
                  getMeta={(item) => item.reference || item.type}
                  emptyLabel="No inventory items match."
                />
              ) : (
                <p className="text-sm sp-muted mt-2">No inventory items available.</p>
              )}
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Attachments</label>
              <input
                type="file"
                multiple
                className="sp-input mt-2 w-full"
                onChange={(e) => setIncidentFiles(Array.from(e.target.files || []))}
              />
              {incidentFiles.length > 0 && (
                <div className="text-xs sp-muted mt-2">
                  {incidentFiles.map((file) => file.name).join(', ')}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setIncidentModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleIncidentSubmit}>
              Save Incident
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showIncidentInventoryModal} onClose={() => setShowIncidentInventoryModal(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>Add Inventory Item</h3>
            <Button variant="ghost" size="sm" onClick={() => setShowIncidentInventoryModal(false)} type="button">
              Close
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="sp-label">Item name</label>
              <input
                value={newIncidentInventoryItem.name}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, name: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Item name"
              />
            </div>
            <div>
              <label className="sp-label">Type</label>
              <input
                value={newIncidentInventoryItem.type}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, type: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Type"
              />
            </div>
            <div>
              <label className="sp-label">Condition</label>
              <input
                value={newIncidentInventoryItem.condition}
                onChange={(e) => setNewIncidentInventoryItem({ ...newIncidentInventoryItem, condition: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="Condition"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setShowIncidentInventoryModal(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={handleAddIncidentInventoryItem}>
              Add Item
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={reviewModalOpen} onClose={() => setReviewModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
              Add Support Review
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setReviewModalOpen(false)} type="button">
              Close
            </Button>
          </div>
          {reviewError && (
            <div className="sp-alert sp-alert--critical text-sm">
              {reviewError}
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="sp-label">Title</label>
              <input
                value={reviewForm.title}
                onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                className="sp-input mt-2 w-full"
                placeholder="e.g. Monthly support review"
              />
            </div>
            <div>
              <label className="sp-label">Due date</label>
              <DateSelect
                value={reviewForm.dueDate}
                onChange={(value) => setReviewForm({ ...reviewForm, dueDate: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Assigned to</label>
              <SearchableSelect
                value={reviewForm.assignedTo}
                options={[{ value: '', label: 'Unassigned' }, ...supportWorkerSelectOptions]}
                placeholder="Search staff"
                emptyLabel="No staff found."
                ariaLabel="Search review owner"
                className="mt-2"
                onChange={(value) => setReviewForm({ ...reviewForm, assignedTo: value })}
              />
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={reviewForm.status}
                onChange={(e) => setReviewForm({ ...reviewForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="open">Open</option>
                <option value="completed">Completed</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Notes</label>
              <textarea
                value={reviewForm.notes}
                onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })}
                className="sp-textarea mt-2 w-full"
                rows={3}
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setReviewModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={submitReview}>
              Save Review
            </Button>
          </div>
        </div>
      </Modal>

      <Modal isOpen={attendanceModalOpen} onClose={() => setAttendanceModalOpen(false)}>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="sp-value" style={{ fontSize: '16px', fontWeight: 600 }}>
              Mark Attendance
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setAttendanceModalOpen(false)} type="button">
              Close
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="sp-label">Date</label>
              <DateSelect
                value={attendanceForm.date}
                onChange={(value) => setAttendanceForm({ ...attendanceForm, date: value })}
                className="mt-2 w-full"
              />
            </div>
            <div>
              <label className="sp-label">Status</label>
              <select
                value={attendanceForm.status}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, status: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="attended">Attended</option>
                <option value="partially attended">Partially attended</option>
                <option value="late">Late arrival</option>
                <option value="disrupted">Disrupted</option>
                <option value="session stopped">Session stopped</option>
                <option value="authorised absence">Authorised absence</option>
                <option value="unauthorised absence">Unauthorised absence</option>
              </select>
            </div>
            <div>
              <label className="sp-label">Programme (optional)</label>
              <select
                value={attendanceForm.programme}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, programme: e.target.value })}
                className="sp-select mt-2 w-full"
              >
                <option value="">Select programme</option>
                {student.enrolments.map((enrol) => (
                  <option key={enrol.id} value={enrol.programme}>
                    {enrol.programme}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="sp-label">Notes</label>
              <textarea
                value={attendanceForm.notes}
                onChange={(e) => setAttendanceForm({ ...attendanceForm, notes: e.target.value })}
                className="sp-textarea mt-2 w-full"
                rows={3}
                placeholder="Optional notes"
              />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={() => setAttendanceModalOpen(false)} type="button">
              Cancel
            </Button>
            <Button size="sm" onClick={submitAttendanceLog}>
              Save Attendance
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
